import type { Customer } from "@/views/UnauthorizedView/types";
import { Features, FEATURE_CONFIG, AccessRoleTypes } from "@/router/types";

/**
 * Enum for different product types
 */
export enum ProductType {
  VISIBILITY = "visibility",
  EMISSIONS = "emissions",
  RISK_MANAGEMENT = "riskManagement"
}

/**
 * Metadata for each product type
 */
export interface ProductMetadata {
  type: ProductType;
  displayName: string;
  testId: string;
  checkFlags: (keyof Customer)[];
}

/**
 * Centralized product configuration - add new products here
 */
export const PRODUCT_METADATA: ProductMetadata[] = [
  {
    type: ProductType.VISIBILITY,
    displayName: "Visibility",
    testId: "visibility-product",
    checkFlags: ["uiEnabled", "dndEnabled", "disruptionEnabled", "dndExportEnabled", "airEnabled"]
  },
  {
    type: ProductType.EMISSIONS,
    displayName: "Emissions",
    testId: "emissions-product",
    checkFlags: ["emissionEnabled"]
  },
  {
    type: ProductType.RISK_MANAGEMENT,
    displayName: "Risk Management",
    testId: "risk-management-product",
    checkFlags: ["mrmEnabled"]
  }
];

/**
 * Checks if a specific product is enabled for a customer
 */
export const isProductEnabled = (customer: Customer | null | undefined, productType: ProductType): boolean => {
  if (!customer) return false;

  const productMetadata = PRODUCT_METADATA.find((p) => p.type === productType);
  if (!productMetadata) return false;

  return productMetadata.checkFlags.some((flag) => customer[flag] === true);
};

/**
 * Checks if any product is enabled for a customer
 */
export const hasAnyProductEnabled = (customer: Customer | null | undefined): boolean => {
  if (!customer) return false;

  return Object.values(ProductType).some((productType) => isProductEnabled(customer, productType));
};

/**
 * Gets all product statuses for a customer
 */
export const getProductStatuses = (customer: Customer | null | undefined) => {
  const statuses: Record<string, boolean> = {};

  PRODUCT_METADATA.forEach((product) => {
    statuses[product.type] = isProductEnabled(customer, product.type);
  });

  return statuses;
};

/**
 * Gets all products with their metadata and status
 */
export const getAllProductsWithStatus = (customer: Customer | null | undefined) => {
  if (!customer) return [];
  return PRODUCT_METADATA.map((product) => ({
    ...product,
    enabled: isProductEnabled(customer, product.type)
  }));
};

/**
 * Interface for user permission data
 */
export interface UserPermission {
  name: string;
  read: boolean;
  write?: boolean;
}

/**
 * Checks if user has required role for a specific feature
 */
export const hasRequiredRole = (
  feature: Features,
  roleType: AccessRoleTypes,
  userRoles: string[] | undefined
): boolean => {
  if (!userRoles?.length) return false;

  const config = FEATURE_CONFIG[feature];
  if (!config?.requiredRoles) return false;

  const matchingRoles = config.requiredRoles.filter((role) => role.endsWith(`.${roleType.toLowerCase()}`));
  return matchingRoles.some((role) => userRoles.includes(role));
};

/**
 * Checks if a feature has write roles defined in its configuration
 */
export const featureHasWriteRole = (feature: Features): boolean => {
  const config = FEATURE_CONFIG[feature];
  return config?.requiredRoles?.some((role) => role.endsWith(".write")) || false;
};

/**
 * Determines enabled Visibility features based on customer flags
 */
export const getEnabledVisibilityFeatures = (
  customer: Customer | null | undefined
): {
  shipments: boolean;
  dnd: boolean;
  portCongestion: boolean;
  airShipments: boolean;
} => {
  if (!customer) {
    return { shipments: false, dnd: false, portCongestion: false, airShipments: false };
  }

  return {
    shipments: Boolean(customer.uiEnabled),
    dnd: Boolean(customer.dndEnabled),
    portCongestion: Boolean(customer.disruptionEnabled),
    airShipments: Boolean(customer.airEnabled)
  };
};

/**
 * Creates a permission object for a feature
 */
const createPermission = (
  name: string,
  feature: Features,
  userRoles: string[] | undefined,
  readValue: boolean
): UserPermission => ({
  name,
  read: readValue,
  write: featureHasWriteRole(feature) ? hasRequiredRole(feature, AccessRoleTypes.WRITE, userRoles) : undefined
});

/**
 * Features to display in user permissions list
 */
const PERMISSION_FEATURES: Features[] = [
  Features.SHIPMENTS,
  Features.DND,
  Features.PORT_CONGESTION,
  Features.AIR_SHIPMENTS,
  Features.EMISSIONS,
  Features.RISK_MANAGEMENT
];

/**
 * Display names for features
 */
const FEATURE_DISPLAY_NAMES: Partial<Record<Features, string>> = {
  [Features.SHIPMENTS]: "Shipments",
  [Features.DND]: "DnD",
  [Features.PORT_CONGESTION]: "Port Congestion",
  [Features.AIR_SHIPMENTS]: "Air Shipments",
  [Features.EMISSIONS]: "Emissions",
  [Features.RISK_MANAGEMENT]: "Risk Management"
};

/**
 * Gets user permissions based on user roles - shows all roles status always
 */
export const getUserPermissions = (userRoles: string[] | undefined): UserPermission[] => {
  return PERMISSION_FEATURES.map((feature) =>
    createPermission(
      FEATURE_DISPLAY_NAMES[feature] || feature,
      feature,
      userRoles,
      hasRequiredRole(feature, AccessRoleTypes.READ, userRoles)
    )
  );
};
